/**
 * Tab 03 · Patterns — the whole model as a list, plus the pattern screen.
 *
 * One list, not a "mine / all" switch: the whole model is 53 patterns and the
 * user's own ones are simply lit. Hypotheses — patterns from a direction the
 * quiz never asked about — sit in the same list with a dashed bar and no number,
 * because a grey "still measuring" is honest while an invented percent is not.
 *
 * Desktop is master-detail: the list stays on the left rail and selecting a row
 * swaps the panel on the right without a navigation. Mobile pushes a screen.
 */
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { useT, useTt, TFn } from '../i18n/useT';
import { AppShell, DeskRow } from './AppShell';
import { useAppContent, AppContent, Pattern } from './content';
import { useProfile, Profile, PatState } from './profile';
import { Icon } from './icons';
import { XpTag } from './XpTag';
import {
    Acc,
    Appear,
    Body,
    Btn,
    Chip,
    Col,
    Fluid,
    H1,
    Meta,
    Muted,
    Panel,
    PolTag,
    Rail,
    Row,
    Sec,
    SegBarC,
    useIsDesktop,
} from './primitives';
import {
    c,
    hair,
    hair2,
    f,
    ACT,
    scoreColor,
    patternZone,
    effectiveStrength,
    strengthKey,
    LOAD,
    DESKTOP,
    Polarity,
} from './tokens';
import { R } from './routes';

/** Chip order from the handoff — picked so eight labels pack into two tidy rows. */
const FILTER_ORDER = ['behavior', 'biophysiology', 'affect', 'cognition', 'motivation', 'attention', 'social', 'self'];

const BLOCK = '#fb923c';
const ENGINE = '#4ade80';

type PolFilter = 'all' | Polarity;

/** A pattern plus whatever the profile knows about it. `pct: null` = hypothesis. */
interface Item {
    p: Pattern;
    pct: number | null;
    hyp: boolean;
    /** Points completed tasks paid into this pattern — shown even on a
        hypothesis row: an unmeasured direction can still be worked on, and
        hiding those points made the pattern sums disagree with Dynamic. */
    earned: number;
}

const neutralZone = { hex: 'rgba(232,230,224,0.40)', soft: 'transparent', key: 'app.pattern.collecting' };

const zoneOf = (it: Item) => (it.hyp ? neutralZone : patternZone(it.pct as number, it.p.pol));

/** Ranked by the inverted value, so "needs work most" is what floats to the top. */
const rank = (it: Item) => (it.hyp ? -1 : effectiveStrength(it.pct as number, it.p.pol));

function buildItems(content: AppContent, profile: Profile): Item[] {
    const by = new Map(profile.patterns.map((s) => [s.code, s] as const));

    return content.patterns.map((p) => {
        const st = by.get(p.code);
        const pct = st?.pct ?? null;

        return { p, pct, hyp: pct == null, earned: st?.earned || 0 };
    });
}

const DirFilter: React.FC<{
    content: AppContent;
    profile: Profile;
    active: string;
    onPick: (code: string) => void;
    total: number;
    pad: string;
}> = ({ content, profile, active, onPick, total, pad }) => {
    const t = useT();

    return (
        <Row style={{ gap: 5, padding: pad, flexWrap: 'wrap' }}>
        <button
            type="button"
            onClick={() => onPick('all')}
            style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
        >
            <Chip
                style={{
                    background: active === 'all' ? c.textPri : c.card,
                    color: active === 'all' ? '#0a0a0a' : c.muted,
                    border: active === 'all' ? `1px solid ${c.textPri}` : hair,
                    padding: '6px 11px',
                    fontSize: 10,
                }}
            >
                {t('app.pats.filter.all', { n: total })}
            </Chip>
        </button>
        {FILTER_ORDER.map((code) => {
            const d = content.dirBy[code];
            const st = profile.directions.find((x) => x.code === code);
            const on = active === code;
            const n = (content.patsByDim[code] || []).length;

            if (!d) return null;

            return (
                <button
                    key={code}
                    type="button"
                    onClick={() => onPick(code)}
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                >
                    <Row
                        style={{
                            gap: 5,
                            padding: '6px 10px',
                            borderRadius: 99,
                            background: on ? 'rgba(232,230,224,0.08)' : c.card,
                            border: on ? '1px solid rgba(232,230,224,0.3)' : hair,
                        }}
                    >
                        <Icon
                            k={code}
                            size={11}
                            color={st?.deep ? scoreColor(st.score).hex : 'rgba(232,230,224,0.34)'}
                        />
                        <Meta style={{ color: on ? c.textPri : c.muted, fontSize: 9.5, letterSpacing: '0.1em' }}>
                            {d.disp}
                        </Meta>
                        <span style={{ fontFamily: f.mono, fontSize: 9, color: c.subtle }}>{n}</span>
                    </Row>
                </button>
            );
        })}
        </Row>
    );
};

const PolChip: React.FC<{ label: string; col: string; n: number; on: boolean; onClick: () => void }> = ({
    label,
    col,
    n,
    on,
    onClick,
}) => (
    <button
        type="button"
        onClick={onClick}
        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
    >
        <Row
            style={{
                gap: 6,
                padding: '5px 10px',
                borderRadius: 99,
                border: `1px solid ${col}${on ? '88' : '33'}`,
                background: `${col}${on ? '22' : '0f'}`,
            }}
        >
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: col }} />
            <Meta style={{ fontSize: 9.5, color: col, letterSpacing: '0.1em' }}>{label}</Meta>
            <span style={{ fontFamily: f.mono, fontSize: 9.5, color: c.subtle }}>{n}</span>
        </Row>
    </button>
);

/** Mobile list row. */
const MineRow: React.FC<{ it: Item; dirName: string; onOpen: () => void }> = ({ it, dirName, onOpen }) => {
    const { t, tn } = useTt();
    const z = zoneOf(it);

    return (
        <button
            type="button"
            onClick={onOpen}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderBottom: hair2,
                font: 'inherit',
                color: 'inherit',
                padding: '15px 20px 14px',
                cursor: 'pointer',
            }}
        >
            <Row style={{ gap: 11 }}>
                <span
                    style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        flex: '0 0 auto',
                        background: it.hyp ? 'transparent' : z.hex,
                        border: it.hyp ? '1px solid rgba(232,230,224,0.28)' : 'none',
                        boxShadow: it.hyp ? 'none' : `0 0 7px ${z.hex}`,
                    }}
                />
                <span
                    style={{
                        fontSize: 16.5,
                        fontWeight: 400,
                        letterSpacing: '-0.02em',
                        color: it.hyp ? c.text : c.textPri,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {it.p.name}
                </span>
                <Meta style={{ fontSize: 10, color: c.muted }}>{dirName}</Meta>
                <Icon k="chev" size={12} color={c.subtle} />
            </Row>
            <Muted
                style={{
                    fontSize: 12.5,
                    margin: '8px 0 0 18px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {it.p.sub}
            </Muted>
            <Col style={{ margin: '11px 0 0 18px' }} gap={7}>
                <Row style={{ gap: 11 }}>
                    {it.hyp ? (
                        <div
                            style={{ flex: 1, height: 5, borderRadius: 3, border: '1px dashed rgba(232,230,224,0.2)' }}
                        />
                    ) : (
                        <SegBarC pct={rank(it)} hex={z.hex} n={12} />
                    )}
                    <span
                        style={{
                            fontFamily: f.mono,
                            fontSize: 15,
                            fontWeight: 300,
                            letterSpacing: '-0.02em',
                            color: it.hyp ? c.subtle : c.textPri,
                            width: 22,
                            textAlign: 'right',
                        }}
                    >
                        {it.hyp ? '–' : Math.round(it.pct as number)}
                    </span>
                </Row>
                <Row style={{ justifyContent: 'space-between' }}>
                    <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em', color: it.hyp ? c.subtle : z.hex }}>
                        {t(it.hyp ? 'app.pattern.collecting' : strengthKey(it.pct as number, it.p.pol))}
                    </Meta>
                    <Row style={{ gap: 9 }}>
                        {!!it.earned && (
                            <Meta style={{ fontSize: 9, letterSpacing: '0.08em', color: ACT }}>
                                +{tn('app.state.points', it.earned)}
                            </Meta>
                        )}
                        <Meta style={{ fontSize: 9, letterSpacing: '0.1em', color: c.subtle }}>
                            {t(it.p.pol === 'engine' ? 'app.pol.engine' : 'app.pol.shadow')}
                        </Meta>
                    </Row>
                </Row>
            </Col>
        </button>
    );
};

/** Desktop rail row — compact, selectable. */
const RailRow: React.FC<{ it: Item; dirName: string; on: boolean; onPick: () => void }> = ({
    it,
    dirName,
    on,
    onPick,
}) => {
    const z = zoneOf(it);
    const { earned } = it;

    return (
        <button
            type="button"
            onClick={onPick}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                cursor: 'pointer',
                padding: '13px 14px',
                margin: '6px -6px',
                borderRadius: 13,
                background: on ? `${z.hex}12` : 'transparent',
                border: on ? `1px solid ${z.hex}44` : '1px solid transparent',
            }}
        >
            <Row style={{ gap: 10 }}>
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        flex: '0 0 auto',
                        background: it.hyp ? 'transparent' : z.hex,
                        border: it.hyp ? '1px solid rgba(232,230,224,0.28)' : 'none',
                        boxShadow: it.hyp ? 'none' : `0 0 7px ${z.hex}`,
                    }}
                />
                <span
                    style={{
                        fontSize: 14,
                        color: it.hyp ? c.text : c.textPri,
                        fontWeight: 400,
                        flex: 1,
                        minWidth: 0,
                        letterSpacing: '-0.01em',
                    }}
                >
                    {it.p.name}
                </span>
                {!!earned && <Meta style={{ fontSize: 9, color: ACT, letterSpacing: '0.08em' }}>+{earned}</Meta>}
                <Meta style={{ fontSize: 9, color: c.muted }}>{dirName}</Meta>
            </Row>
            <Row style={{ gap: 11, marginTop: 10, marginLeft: 16 }}>
                {it.hyp ? (
                    <div style={{ flex: 1, height: 5, borderRadius: 3, border: '1px dashed rgba(232,230,224,0.2)' }} />
                ) : (
                    <SegBarC pct={rank(it)} hex={z.hex} n={10} />
                )}
                <span
                    style={{
                        fontFamily: f.mono,
                        fontSize: 12.5,
                        color: it.hyp ? c.subtle : c.textPri,
                        width: 20,
                        textAlign: 'right',
                    }}
                >
                    {it.hyp ? '–' : Math.round(it.pct as number)}
                </span>
            </Row>
        </button>
    );
};

/** The pattern itself — shared by the desktop detail panel and the mobile screen. */
const PatternBody: React.FC<{
    it: Item;
    content: AppContent;
    profile: Profile;
    wide?: boolean;
}> = ({ it, content, profile, wide }) => {
    const { t, tn } = useTt();
    const { p } = it;
    const z = zoneOf(it);
    const hasMech = !!p.mech?.length;
    const hasRes = !!p.res?.length;
    const d = content.dirBy[p.dim];
    const dirState = profile.directions.find((x) => x.code === p.dim);
    const { earned } = it;
    const exs = (content.exsByPattern[p.code] || []).slice(0, 2);
    const scoreOf = (dir: string) => profile.directions.find((x) => x.code === dir)?.score ?? null;

    return (
        <>
            <Row style={{ gap: 8, flexWrap: 'wrap' }}>
                <PolTag pol={p.pol} />
                {!it.hyp && (
                    <Chip color={z.hex} border={`${z.hex}44`}>
                        {t(strengthKey(it.pct as number, p.pol))}
                    </Chip>
                )}
                {it.hyp && <Chip color={c.subtle}>{t('app.pats.dirNotTaken')}</Chip>}
                {!!earned && (
                    <Chip color={ACT} border={`${ACT}44`}>
                        {t('app.dir.earned', { n: earned })}
                    </Chip>
                )}
                <Row style={{ gap: 7, marginLeft: 'auto' }}>
                    <Icon k={p.dim} size={12} color={dirState?.deep ? scoreColor(dirState.score).hex : c.subtle} />
                    <Meta style={{ fontSize: 9.5 }}>
                        {d?.disp}
                        {dirState?.deep ? ` · ${dirState.score}` : ''}
                    </Meta>
                </Row>
            </Row>

            <Row style={{ gap: 26, marginTop: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <h1
                    style={{
                        fontSize: wide ? 30 : 27,
                        fontWeight: 300,
                        letterSpacing: '-0.03em',
                        lineHeight: 1.06,
                        margin: 0,
                        color: c.textPri,
                        flex: '1 1 260px',
                    }}
                >
                    {p.name}
                </h1>
                <Col style={{ flex: '1 1 220px', minWidth: 0 }} gap={7}>
                    <Row style={{ gap: 11 }}>
                        {it.hyp ? (
                            <div
                                style={{
                                    flex: 1,
                                    height: 5,
                                    borderRadius: 3,
                                    border: '1px dashed rgba(232,230,224,0.2)',
                                }}
                            />
                        ) : (
                            <SegBarC pct={rank(it)} hex={z.hex} n={12} />
                        )}
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 15,
                                fontWeight: 300,
                                color: it.hyp ? c.subtle : c.textPri,
                                width: 22,
                                textAlign: 'right',
                            }}
                        >
                            {it.hyp ? '–' : Math.round(it.pct as number)}
                        </span>
                    </Row>
                    {/* Right-aligned under the number, not under the bar: the
                        caption belongs to the figure it qualifies. `Meta` is a
                        span, so it needs `display: block` for textAlign to bite.

                        Impact on the index needs 90 days of logs; until then it
                        says so rather than printing a modelled percentage. */}
                    {!!earned && (
                        <Meta
                            style={{
                                display: 'block',
                                textAlign: 'right',
                                fontSize: 9,
                                letterSpacing: '0.1em',
                                color: ACT,
                            }}
                        >
                            {t('app.dir.earned', { n: earned })}
                        </Meta>
                    )}
                    <Meta
                        style={{
                            display: 'block',
                            textAlign: 'right',
                            fontSize: 8.5,
                            letterSpacing: '0.1em',
                            color: c.subtle,
                        }}
                    >
                        {t('app.pats.indexImpact')}
                    </Meta>
                </Col>
            </Row>

            <div
                style={{
                    marginTop: 20,
                    padding: '18px 20px',
                    borderRadius: 16,
                    border: `1px solid ${z.hex}30`,
                    background: `linear-gradient(140deg, ${z.hex}0f, ${c.cardIn} 60%)`,
                }}
            >
                <Row style={{ gap: 9, marginBottom: 11 }}>
                    <Icon k="book" size={13} color={z.hex} />
                    <Meta style={{ color: z.hex, fontSize: 9.5, letterSpacing: '0.16em' }}>
                        {t('app.pats.takeaway')}
                    </Meta>
                </Row>
                <div
                    style={{
                        fontFamily: f.serif,
                        fontStyle: 'italic',
                        fontWeight: 300,
                        fontSize: wide ? 19 : 18,
                        lineHeight: 1.38,
                        color: c.textPri,
                    }}
                >
                    {p.aha}
                </div>
            </div>

            <Body style={{ fontSize: 14.5, marginTop: 18 }}>{p.sub}</Body>

            <div style={{ marginTop: 18 }}>
                {/* Pattern-level mechanism prose exists for 11 of 53. For the rest
                    the block still opens the screen — it just carries the
                    direction's mechanism, attributed, instead of nothing. The
                    direction is the system this pattern sits inside, so the text
                    is true of it; the note stops it reading as bespoke. */}
                <Acc
                    title={t('app.pats.how')}
                    meta={
                        hasMech
                            ? t('app.pats.how.meta')
                            : t('app.pats.how.metaFallback', { dir: d?.disp || '' })
                    }
                    open
                    hex={z.hex}
                >
                    <Col gap={12}>
                        {(hasMech ? p.mech || [] : d?.theory || []).map((para) => (
                            <Body key={para.slice(0, 24)} style={{ fontSize: 13.5 }}>
                                {para}
                            </Body>
                        ))}
                    </Col>
                    {!hasMech && (
                        <Row
                            style={{
                                gap: 9,
                                marginTop: 14,
                                padding: '11px 13px',
                                background: c.cardIn,
                                borderRadius: 12,
                                border: hair,
                                alignItems: 'flex-start',
                            }}
                        >
                            <Icon k="info" size={14} color={c.subtle} style={{ marginTop: 1, flex: '0 0 auto' }} />
                            <Muted style={{ fontSize: 12 }}>{t('app.pats.how.fallbackNote')}</Muted>
                        </Row>
                    )}
                </Acc>
                <Acc
                    title={t('app.pats.shows')}
                    meta={tn('app.pats.shows.meta', p.shows.length)}
                    hex={z.hex}
                    last={!hasRes}
                >
                    <Col gap={10}>
                        {p.shows.map((line) => (
                            <Row key={line.slice(0, 24)} style={{ gap: 11, alignItems: 'flex-start' }}>
                                <span
                                    style={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: '50%',
                                        background: z.hex,
                                        marginTop: 7,
                                        flex: '0 0 auto',
                                    }}
                                />
                                <Body style={{ fontSize: 13.5 }}>{line}</Body>
                            </Row>
                        ))}
                    </Col>
                </Acc>
                {hasRes && (
                    <Acc
                        title={t('app.dir.helps')}
                        meta={tn('app.dir.helps.meta', p.res?.length || 0)}
                        hex={z.hex}
                        last
                    >
                        <Col gap={10}>
                            {(p.res || []).map((line) => (
                                <Row key={line.slice(0, 24)} style={{ gap: 11, alignItems: 'flex-start' }}>
                                    <Icon
                                        k="check"
                                        size={13}
                                        color={ENGINE}
                                        style={{ marginTop: 3, flex: '0 0 auto' }}
                                    />
                                    <Body style={{ fontSize: 13.5 }}>{line}</Body>
                                </Row>
                            ))}
                        </Col>
                    </Acc>
                )}
            </div>

            {exs.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.pats.tasks')}</Meta>
                        <Meta style={{ fontSize: 9.5 }}>{t('app.pats.tasksAvailable', { n: exs.length })}</Meta>
                    </Row>
                    <Col gap={9}>
                        {exs.map((e, i) => (
                            <Link key={e.code} to={R.task(e.code)} style={{ textDecoration: 'none' }}>
                                <div
                                    style={{
                                        border: i === 0 ? `1px solid ${z.hex}3d` : hair,
                                        borderRadius: 14,
                                        padding: '13px 14px',
                                        background: c.cardIn,
                                    }}
                                >
                                    <Row style={{ gap: 10 }}>
                                        <span
                                            style={{
                                                fontSize: 13.5,
                                                color: c.textPri,
                                                fontWeight: 400,
                                                letterSpacing: '-0.01em',
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {e.name}
                                        </span>
                                        <Icon k="chev" size={12} color={c.subtle} />
                                    </Row>
                                    <Meta style={{ fontSize: 9, display: 'block', marginTop: 8 }}>
                                        {e.min ? `${t('app.task.min', { n: e.min })} · ` : ''}
                                        {t(LOAD[e.load] || e.load)}
                                    </Meta>
                                    <XpTag ex={e} content={content} scoreOf={scoreOf} style={{ marginTop: 10 }} />
                                </div>
                            </Link>
                        ))}
                    </Col>
                </div>
            )}

            <Link to={R.dir(p.dim)} style={{ textDecoration: 'none' }}>
                <Row
                    style={{
                        gap: 11,
                        marginTop: 20,
                        background: c.cardIn,
                        border: hair,
                        borderRadius: 15,
                        padding: '13px 15px',
                    }}
                >
                    <Icon k={p.dim} size={15} color={dirState?.deep ? scoreColor(dirState.score).hex : c.subtle} />
                    <span style={{ fontSize: 13, color: c.text, fontWeight: 300, flex: 1, minWidth: 0 }}>
                        {t('app.pats.backTo')} <span style={{ color: c.textPri }}>{d?.disp}</span>
                        {dirState?.deep ? ` · ${dirState.score}` : ''}
                    </span>
                    <Icon k="chev" size={12} color={c.subtle} />
                </Row>
            </Link>
        </>
    );
};

/* ── list ──────────────────────────────────────────── */

export const PatternsPage: React.FC = () => {
    const { user, userProfile } = useAuth();
    const { lang } = useLang();
    const { t, tn } = useTt();
    const navigate = useNavigate();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const desktop = useIsDesktop(DESKTOP);
    const [dirFilter, setDirFilter] = React.useState('all');
    const [polFilter, setPolFilter] = React.useState<PolFilter>('all');
    const [selected, setSelected] = React.useState<string | null>(null);

    const items = React.useMemo(() => (content ? buildItems(content, profile) : []), [content, profile]);

    const shown = React.useMemo(() => {
        const list = items
            .filter((it) => (dirFilter === 'all' ? true : it.p.dim === dirFilter))
            .filter((it) => (polFilter === 'all' ? true : it.p.pol === polFilter));

        // Measured first, ranked by need; hypotheses keep the model visible below.
        return [...list].sort((a, b) => rank(b) - rank(a));
    }, [items, dirFilter, polFilter]);

    const nBlock = items.filter((i) => i.p.pol !== 'engine').length;
    const nEngine = items.length - nBlock;
    const current = shown.find((it) => it.p.code === selected) || shown.find((it) => !it.hyp) || shown[0];

    const shellProps = {
        active: 'pats' as const,
        title: t('app.tab.pats'),
        subtitle: t('app.pats.subtitle', { n: items.length }),
        index: profile.index,
        email: userProfile?.email || user?.email,
        chips: (
            <>
                <PolChip
                    label={t('app.pats.blockers')}
                    col={BLOCK}
                    n={nBlock}
                    on={polFilter === 'shadow'}
                    onClick={() => setPolFilter((v) => (v === 'shadow' ? 'all' : 'shadow'))}
                />
                <PolChip
                    label={t('app.pats.drivers')}
                    col={ENGINE}
                    n={nEngine}
                    on={polFilter === 'engine'}
                    onClick={() => setPolFilter((v) => (v === 'engine' ? 'all' : 'engine'))}
                />
            </>
        ),
    };

    if (!content) {
        return (
            <AppShell {...shellProps}>
                <Col style={{ padding: '60px 20px', alignItems: 'center' }}>
                    <Meta style={{ color: c.subtle }}>{t('app.state.loading')}</Meta>
                </Col>
            </AppShell>
        );
    }

    if (desktop) {
        return (
            <AppShell {...shellProps}>
                <Appear>
                    <DirFilter
                        content={content}
                        profile={profile}
                        active={dirFilter}
                        onPick={setDirFilter}
                        total={items.length}
                        pad="18px 34px 0"
                    />
                    <DeskRow>
                        <Rail width={392}>
                            <Panel
                                title={
                                    dirFilter === 'all'
                                        ? t('app.pats.allPatterns')
                                        : content.dirBy[dirFilter]?.disp.toUpperCase()
                                }
                                right={<Meta style={{ fontSize: 9.5 }}>{t('app.pats.sortDesc')}</Meta>}
                                pad="6px 18px 14px"
                            >
                                {shown.length ? (
                                    shown.map((it) => (
                                        <RailRow
                                            key={it.p.code}
                                            it={it}
                                            dirName={content.dirBy[it.p.dim]?.disp || it.p.dim}
                                            on={current?.p.code === it.p.code}
                                            onPick={() => setSelected(it.p.code)}
                                        />
                                    ))
                                ) : (
                                    <Muted style={{ fontSize: 12.5, padding: '14px 0' }}>
                                        {t('app.pats.filter.empty')}
                                    </Muted>
                                )}
                            </Panel>
                        </Rail>

                        <Fluid>
                            {current ? (
                                <Panel pad="22px 24px 24px">
                                    <PatternBody it={current} content={content} profile={profile} wide />
                                </Panel>
                            ) : (
                                <Panel pad="30px 24px">
                                    <Muted>{t('app.pats.pickOne')}</Muted>
                                </Panel>
                            )}
                        </Fluid>
                    </DeskRow>
                </Appear>
            </AppShell>
        );
    }

    return (
        <AppShell {...shellProps}>
            <Appear>
                <Col style={{ padding: '10px 20px 0' }} gap={11}>
                    <H1>{t('app.tab.pats')}</H1>
                    <Muted>
                        {tn('app.pats.intro.lead', items.length)}{' '}
                        <span style={{ color: BLOCK }}>{t('app.pats.intro.blockers')}</span>
                        {t('app.pats.intro.mid')}
                        <span style={{ color: ENGINE }}>{t('app.pats.intro.drivers')}</span>
                        {t('app.pats.intro.tail')}
                    </Muted>
                </Col>

                <DirFilter
                    content={content}
                    profile={profile}
                    active={dirFilter}
                    onPick={setDirFilter}
                    total={items.length}
                    pad="16px 20px 0"
                />

                <Row style={{ margin: '16px 20px 0', padding: '10px 0', borderTop: hair, borderBottom: hair, gap: 8 }}>
                    <Row style={{ gap: 7 }}>
                        <PolChip
                            label={t('app.pats.blockers')}
                            col={BLOCK}
                            n={nBlock}
                            on={polFilter === 'shadow'}
                            onClick={() => setPolFilter((v) => (v === 'shadow' ? 'all' : 'shadow'))}
                        />
                        <PolChip
                            label={t('app.pats.drivers')}
                            col={ENGINE}
                            n={nEngine}
                            on={polFilter === 'engine'}
                            onClick={() => setPolFilter((v) => (v === 'engine' ? 'all' : 'engine'))}
                        />
                    </Row>
                    <Row style={{ gap: 5, marginLeft: 'auto' }}>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.1em' }}>{t('app.pats.sort')}</Meta>
                        <Icon k="down" size={11} color={c.muted} />
                    </Row>
                </Row>

                {shown.map((it) => (
                    <MineRow
                        key={it.p.code}
                        it={it}
                        dirName={content.dirBy[it.p.dim]?.disp || it.p.dim}
                        onOpen={() => navigate(R.pattern(it.p.code))}
                    />
                ))}

                <Muted style={{ fontSize: 12, padding: '18px 20px 24px' }}>{t('app.pats.outro')}</Muted>
            </Appear>
        </AppShell>
    );
};

/* ── pattern screen (mobile push) ──────────────────── */

export const PatternPage: React.FC = () => {
    const { code = '' } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    const { lang } = useLang();
    const t = useT();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);

    const p = content?.patBy[code];
    const st = profile.patterns.find((x) => x.code === code);
    const it: Item | null = p
        ? { p, pct: st?.pct ?? null, hyp: (st?.pct ?? null) == null, earned: st?.earned || 0 }
        : null;
    const z = it ? zoneOf(it) : null;

    if (!content || !p || !it) {
        return (
            <AppShell active="pats" title={t('app.pats.one')}>
                <Col style={{ padding: '60px 20px', alignItems: 'center' }} gap={10}>
                    <Meta style={{ color: c.subtle }}>
                        {t(content ? 'app.pats.notFound' : 'app.state.loading')}
                    </Meta>
                    {content && <Btn onClick={() => navigate(R.pats)}>{t('app.pats.backToList')}</Btn>}
                </Col>
            </AppShell>
        );
    }

    return (
        <AppShell
            active="pats"
            title={p.name}
            subtitle={`${t('app.pats.one').toUpperCase()} · ${content.dirBy[p.dim]?.disp.toUpperCase() || ''}`}
            index={profile.index}
            email={userProfile?.email || user?.email}
            mobileHeader={
                <Row style={{ padding: '14px 16px 12px', gap: 10, borderBottom: hair }}>
                    <button
                        type="button"
                        aria-label={t('app.action.back')}
                        onClick={() => navigate(R.pats)}
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            border: hair,
                            background: c.card,
                            display: 'grid',
                            placeItems: 'center',
                            flex: '0 0 auto',
                            cursor: 'pointer',
                        }}
                    >
                        <Icon k="back" size={15} color={c.textPri} />
                    </button>
                    <Col style={{ flex: 1, alignItems: 'center' }} gap={3}>
                        <Meta style={{ color: c.textPri, letterSpacing: '0.18em' }}>
                            {t('app.pats.one').toUpperCase()}
                        </Meta>
                        <Meta style={{ fontSize: 10, color: z?.hex, letterSpacing: '0.12em' }}>
                            {content.dirBy[p.dim]?.disp.toUpperCase()}
                        </Meta>
                    </Col>
                    <div style={{ width: 34, flex: '0 0 auto' }} />
                </Row>
            }
            chips={
                <Link to={R.pats} style={{ textDecoration: 'none' }}>
                    <Meta style={{ fontSize: 10, border: hair, borderRadius: 99, padding: '6px 10px' }}>
                        {t('app.pats.allBack')}
                    </Meta>
                </Link>
            }
        >
            <Appear>
                <Sec title="" style={{ padding: '22px 20px 40px', maxWidth: 720 }}>
                    <PatternBody it={it} content={content} profile={profile} />
                </Sec>
            </Appear>
        </AppShell>
    );
};

export default PatternsPage;
